import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  putClientPolicyApplicationStepRequest,
  postConsentRecordRequest,
  selectPostConsentRecordData,
  selectPostConsentRecordIsLoading,
  selectPostConsentRecordErrorMessage,
  clearPostConsentRecord,
} from '@client/model';
import { getUserIpAddress } from '@/util/getUserIpAddress';
import { useToast } from '@/components/hooks/use-toast';

interface UseIllustrationConsentFlowProps {
  clientPolicyApplicationId: string;
  sourceQuote: PolicyQuote | TraditionalLTCQuote;
  client: Client;
  policyApplication: ClientPolicyApplication;
  onContinue: () => void;
  buildIllustrationMetadata: () => any;
}

export function useIllustrationConsentFlow({
  clientPolicyApplicationId,
  sourceQuote,
  client,
  policyApplication,
  onContinue,
  buildIllustrationMetadata,
}: UseIllustrationConsentFlowProps) {
  const dispatch = useDispatch();
  const { toast } = useToast();

  const postConsentRecordData = useSelector(selectPostConsentRecordData);
  const postConsentRecordIsLoading = useSelector(selectPostConsentRecordIsLoading);
  const postConsentRecordError = useSelector(selectPostConsentRecordErrorMessage);

  const handleContinueWithIllustration = useCallback(async () => {
    if (client && policyApplication) {
      const ipAddress = await getUserIpAddress();

      // Record consent - funding source update happens server-side when illustration completes
      dispatch(
        postConsentRecordRequest({
          clientId: client.clientId,
          clientPolicyApplicationId,
          resourceType: 'ClientPolicyApplicationConsents',
          consentId: 'advisorDownloadPolicyIllustration',
          ipAddress,
          userAgent: navigator.userAgent,
          consentType: 'download',
          metadata: {
            stepType: 'illustration-consent-form',
            clientPolicyApplicationId,
          },
        }),
      );
    }

    // Update application policy details with funding source
  }, [dispatch, clientPolicyApplicationId, client, policyApplication]);

  // Effect to handle consent success and mark step as completed
  useEffect(() => {
    if (postConsentRecordData && !postConsentRecordIsLoading) {
      // Get illustration metadata to save with completion
      const metadata = buildIllustrationMetadata();

      // Mark step as completed with metadata
      dispatch(
        putClientPolicyApplicationStepRequest({
          clientPolicyApplicationId,
          stepType: 'download-policy-illustration',
          status: 'completed',
          metadata,
        }),
      );

      // Clear consent state and continue
      dispatch(clearPostConsentRecord());
      onContinue();
    }
  }, [
    postConsentRecordData,
    postConsentRecordIsLoading,
    dispatch,
    clientPolicyApplicationId,
    onContinue,
    buildIllustrationMetadata,
  ]);

  // Effect to handle consent recording errors
  useEffect(() => {
    if (postConsentRecordError && !postConsentRecordIsLoading) {
      toast({
        title: 'Failed to Record Consent',
        description: 'There was an error recording your consent. Please try again.',
        variant: 'destructive',
      });

      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordError, postConsentRecordIsLoading, toast, dispatch]);

  return {
    handleContinueWithIllustration,
    postConsentRecordIsLoading,
  };
}
